// ModelDetail.tsx

import type { GetRegisteredModelResponse } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import { IBreadcrumbItem } from '@fluentui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { HeaderPure } from '../../components/Header/Header';
import { getRegisteredModelQueryOptions } from '../../mlops/mlops.storage.hooks';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../services/HTTPResponse';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { MLOpsError404Page } from './DeploymentDetail';
import ModelDetailContent from './ModelDetailContent';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';

type ModelDetailParams = { model_id: string; workspace_id: string; action?: string };

const ModelDetail = () => {
  const history = useHistory(),
    params = useParams<ModelDetailParams>();

  const { workspace_id, model_id, action } = params;
  const isTagsManagementPage = params?.model_id === 'manage-tags';

  const isNew = React.useMemo(() => params?.model_id === 'add-new', [params.model_id]);
  const isNewVersionUpload = React.useMemo(() => params?.action === 'add-new-version', [params.action]);
  const workspaceFQDN = workspaceName.compile({ workspace: params.workspace_id });

  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const isExistingModel = !isNew && !!model_id && !!mlopsApiUrl;

  const modelQuery = useQuery<GetRegisteredModelResponse>({
    ...getRegisteredModelQueryOptions(mlopsApiUrl, workspace_id, model_id),
    enabled: isExistingModel,
  });

  const model = modelQuery.data?.registeredModel ?? null;
  const loadingModel = isExistingModel && modelQuery.isLoading;
  const showNotFound = React.useMemo(() => {
    if (modelQuery.isError && modelQuery.error) {
      const err = modelQuery.error;
      return (
        isGrpcGatewayErrorResponse(err) &&
        (err.code === GrpcStatusCode.NOT_FOUND ||
          err.code === GrpcStatusCode.INVALID_ARGUMENT ||
          err.code === GrpcStatusCode.INTERNAL)
      );
    }
    return false;
  }, [modelQuery.isError, modelQuery.error]);

  const breadcrumbs: IBreadcrumbItem[] = [
    {
      key: `home`,
      text: `home`,
      onClick: () => history.push('/'),
    },
    {
      key: `mlops`,
      text: `MLOps`,
      onClick: () => history.push('/mlops'),
    },
    {
      key: 'models',
      text: 'Models',
      onClick: () => history.push(`/mlops/${workspaceFQDN}/models`),
    },
    {
      key: 'model',
      text: isNew ? 'Add model' : isNewVersionUpload ? 'Add new model version' : model?.displayName || 'Model details',
      isCurrentItem: true,
    },
  ];

  if (showNotFound) {
    return MLOpsError404Page(`/mlops/workspaces/${params.workspace_id}/models`, 'Return to all models');
  }

  return isTagsManagementPage ? null : (
    <NavigationWrapper>
      <PageWrapper>
        <HeaderPure
          items={breadcrumbs}
          pageTitle={
            isNew ? 'Add model' : isNewVersionUpload ? 'Add new model version' : model?.displayName || 'Model details'
          }
        />
        <ModelDetailContent
          model={model}
          loadingModel={loadingModel}
          workspaceId={workspace_id}
          modelId={model_id}
          action={action}
          isNew={isNew}
          isNewVersionUpload={isNewVersionUpload}
          workspaceFQDN={workspaceFQDN}
        />
      </PageWrapper>
    </NavigationWrapper>
  );
};
export default ModelDetail;
